

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { deleteBranch, fetchBranches } from "../../../slices/company/branchSlice";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import { Delete, Pen, Users } from "lucide-react";
import Swal from "sweetalert2";
import { fetchTasks, updateTaskStatus } from "../../../slices/company/taskSlice";
import TaskUserListModal from "./users_list_modal";
import moment from "moment";
import { customTableStyles } from "../../../utils/tableStylings";

const TaskAssigned = () => {
    const [showModalUsers, setShowModalUsers] = useState(false);
    const [selectedData, setSelectedData] = useState([]);
    const [selectedFilter, setSelectedFilter] = useState("all"); // Default to "All Task"

    const dispatch = useDispatch();

    // Retrieve branches data and loading/error states from the Redux store
    const { tasks = [] } = useSelector(state => state.tasks);
   
    useEffect(() => {
        dispatch(fetchTasks()); // Fetch tasks when component mounts
    }, [dispatch]);



    const getPriorityClass = (priority) => {
        switch (priority.toLowerCase()) {
            case "low":
                return "bg-primary";
            case "medium":
                return "bg-warning text-dark";
            case "high":
                return "bg-danger";
            default:
                return "bg-secondary";
        }
    };
    
    // Function to return badge class based on status
    const getStatusClass = (status) => {
        switch (status) {
            case "pending":
                return "bg-warning text-dark";
            case "in progress":
                return "bg-primary";
            case "completed":
                return "bg-success";
            default:
                return "bg-secondary";
        }
    };


    const handleStatusChange = (event, row) => {
        const newStatus = event.target.value;
    
        Swal.fire({
            title: "Are you sure?",
            text: `Do you want to change the status to "${newStatus}"?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, change it!",
        }).then(async (result) => {
            const user_id = localStorage.getItem('user_id');
            if (result.isConfirmed) {
                const payload = {
                    id: row.id,
                    status: newStatus,
                    completed_by: newStatus === "completed" ? user_id : '', // Send user_id only if completed
                };
    
                try {
                    await dispatch(updateTaskStatus(payload)).unwrap();
                    Swal.fire("Updated!", "The task status has been updated.", "success");
                } catch (error) {
                    Swal.fire("Error!", "Failed to update status. Please try again.", "error");
                }
            }
        });
    };

    // Define table columns
    const columns = [
        {
            name: "#",
            selector: (row, index) => index + 1, // Display row index (1-based)
            sortable: false,
            width: "50px",
        },
        {
            name: "Task",
            selector: (row) => row.name,
            sortable: true,
        },
        {
            name: "Description",
            selector: (row) => row.description,
            sortable: true,
        },
        {
            name: "Estimate Hour",
            selector: (row) => `${row.estimate_hour} Hour`,
            sortable: true,
        },
        {
            name: "Priority",
            cell: (row) => (
                <span className={`badge text-white ${getPriorityClass(row.priority)}`}>
                    {row.priority}
                </span>
            ),
            sortable: true,
        },
        {
            name: "Start Date",
            selector: (row) => moment(row.start_date).format('DD MMMM, YYYY'),
            sortable: true,
        },
        {
            name: "End Date",
            selector: (row) => moment(row.end_date).format('DD MMMM, YYYY'),
            sortable: true,
        },
        // {
        //     name: "Status",
        //     cell: (row) => (
        //         <span className={`badge text-white ${getStatusClass(row.status)}`}>
        //             {row.status}
        //         </span>
        //     ),
        //     sortable: true,
        // },
        {
            name: "Status",
            cell: (row) => (
                row.status === "completed" ? (
                    <span className={`badge text-white ${getStatusClass(row.status)}`}>
                        {row.status}
                    </span>
                ) : (
                    <select
                        className={`form-select text-white ${getStatusClass(row.status)}`}
                        value={row.status}
                        onChange={(e) => handleStatusChange(e, row)}
                    >
                        {row.status != 'in progress' && (<option value="pending">Pending</option>)}
                        <option value="in progress">In Progress</option>
                        {row.status == 'in progress' && (<option value="completed">Completed</option>)}
                    </select>
                )
            ),
            sortable: true,
        },
        {
            name: "Completed On",
            selector: (row) => row.completed_date?moment(row.completed_date).format('DD MMMM, YYYY'):"Not Completed",
            sortable: true,
        },
        {
            name: "Actions",
            width: "150px",
            cell: (row) => (
                <div>
                    <button
                        className="text-white btn btn-sm bg-primary me-1"
                        onClick={() => handleUserDetail(row)}
                    >
                        <Users/>
                    </button>
            
                        
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];
    
    const handleUserDetail = (data) => {
        setSelectedData(data);
        setShowModalUsers(true);
    }

    const filterTasks = () => {
        const today = moment().startOf("day");

        return tasks.filter((task) => {
            const startDate = moment(task.start_date);
            const endDate = moment(task.end_date);

            switch (selectedFilter) {
                case "today":
                    return startDate.isSameOrBefore(today) && endDate.isSameOrAfter(today);
                case "pending":
                    return endDate.isBefore(today);
                case "upcoming":
                    return startDate.isAfter(today);
                default:
                    return true; // Show all tasks
            }
        });
    };

    return (
        <>
            <div className="row">
                <div className="col-12">
                    <div className="card">  
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h4 className="card-title">Task Assigned</h4>
                            <div className="d-flex justify-content-between gap-2">
                                <select
                                    className="form-select w-auto"
                                    value={selectedFilter}
                                    onChange={(e) => setSelectedFilter(e.target.value)}
                                >
                                    <option value="all">All Task</option>
                                    <option value="today">Today's Task</option>
                                    <option value="pending">Pending Task</option>
                                    <option value="upcoming">Upcoming Task</option>
                                </select>
                                <Link to={'/task/create'} className="btn btn-primary">Create New Task</Link>
                            </div>
                        </div>

                        <div className="card-body">
                    
                            <div className="table-responsive">
                                <DataTable
                                    className="table-hover custom-table"
                                    columns={columns}
                                    data={filterTasks()} // Use filtered data
                                    pagination
                                    striped
                                    customStyles={customTableStyles}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <TaskUserListModal show={showModalUsers} onClose={() => setShowModalUsers(false)} heading={'User Detail'} initialData={selectedData} />
            
        </>
    );
}

export default TaskAssigned;
